import type { MessageDescriptor } from "@lingui/core"
import { msg } from "@lingui/core/macro"
import {
  IconAdjustments,
  IconLanguage,
  IconPalette,
  IconPlugConnected,
  IconReportAnalytics,
  IconServerCog,
  type TablerIcon,
} from "@tabler/icons-react"
import { PLATFORM_GROUP, type TSettingsKeyGroup } from "./key-groups"
import type { TSettingsGroup } from "./section-map"

export { resolveSettingsGroup, SETTINGS_GROUPS, type TSettingsGroup } from "./section-map"

/**
 * The settings rail. Everyday sections come first. Transcription engines, model
 * keys, the analysis prompt, and per-service limits sit behind Advanced.
 * Section ids and the old-id map live in `section-map.ts` so tests can import
 * them without Lingui macros.
 */
export interface ISettingsGroupMeta {
  value: TSettingsGroup
  label: MessageDescriptor
  Icon: TablerIcon
}

export const SETTINGS_GROUP_META: readonly ISettingsGroupMeta[] = [
  { value: "general", label: msg`General`, Icon: IconLanguage },
  { value: "look", label: msg`Look`, Icon: IconPalette },
  { value: "reports", label: msg`Reports`, Icon: IconReportAnalytics },
  { value: "integrations", label: msg`Integrations`, Icon: IconPlugConnected },
  { value: "advanced", label: msg`Advanced`, Icon: IconAdjustments },
]

/**
 * Admin-only group meta: the tenant sections (in rail order) plus the
 * `platform` catch-all used by the super-admin company-override table.
 */
export interface IAdminSettingsGroupMeta {
  value: TSettingsKeyGroup
  label: MessageDescriptor
  Icon: TablerIcon
}

export const ADMIN_SETTINGS_GROUP_META: readonly IAdminSettingsGroupMeta[] = [
  ...SETTINGS_GROUP_META,
  { value: PLATFORM_GROUP, label: msg`Platform & advanced`, Icon: IconServerCog },
]

/**
 * Stable DOM anchors for every concept card. Search results and legacy deep-links
 * resolve to one of these so a jump can scroll the right card into view.
 */
export const SETTINGS_CARD_ANCHORS = {
  transcription: "card-transcription",
  language: "card-language",
  members: "card-members",
  speakerRecognition: "card-speaker-recognition",
  finishedTranscript: "card-finished-transcript",
  llmProviders: "card-llm-providers",
  models: "card-models",
  prompt: "card-prompt",
  image: "card-image-generation",
  reportTypes: "card-report-types",
  summaryLayout: "card-summary-layout",
  budget: "card-budget",
  appearance: "card-appearance",
  integrations: "card-integrations",
  connectClaude: "card-connect-claude",
} as const

export type TSettingsCardAnchor = (typeof SETTINGS_CARD_ANCHORS)[keyof typeof SETTINGS_CARD_ANCHORS]
